import axios from 'axios';
import { toast } from 'react-toastify';

const eopdClient = axios.create({
  baseURL: '/nodeapi/eopd',
});

// Add a request interceptor to include headers for all requests
eopdClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("e-OPD_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const username = localStorage.getItem("e-OPD_username");
    if (username) {
      config.headers.username = username;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Track in-flight refresh promise to prevent multiple simultaneous refresh calls
let refreshTokenPromise = null;

const getStoredRefreshToken = () => {
  let token =
    localStorage.getItem("e-OPD_refresh_token") ||
    localStorage.getItem("refresh_token") ||
    localStorage.getItem("eopd_refresh_token");

  if (!token) {
    try {
      const user = JSON.parse(localStorage.getItem("e-OPD_loggedinUserdetails") || "{}");
      token = user?.refresh_token || user?.refreshToken;
    } catch (e) {}
  }
  if (!token) {
    try {
      const user = JSON.parse(localStorage.getItem("loggedinUserdetails") || "{}");
      token = user?.refresh_token || user?.refreshToken;
    } catch (e) {}
  }

  if (typeof token === "string") {
    token = token.replace(/^["']|["']$/g, "").trim();
  }
  return token || null;
};

const refreshAuthToken = async () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = (async () => {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) {
        console.error(
          "[e-OPD] Token refresh aborted: No refresh token found in localStorage ('e-OPD_refresh_token'). Please log in again to store the refresh token."
        );
        throw new Error("No e-OPD refresh token found in localStorage");
      }

      const username = localStorage.getItem("e-OPD_username");
      const headers = {
        "Content-Type": "application/json",
      };
      if (username) {
        headers.username = username;
      }

      console.log("[e-OPD] Calling /nodeapi/eopd/refresh with refresh token...");
      const response = await axios.post(
        "/nodeapi/eopd/refresh",
        { refresh_token: refreshToken },
        { headers }
      );

      const resData = response?.data;
      if (resData?.success) {
        const newToken = resData?.token || resData?.response?.token || resData?.data?.token;
        if (newToken) {
          localStorage.setItem("e-OPD_token", newToken);
          console.log("[e-OPD] Token refreshed successfully. New token saved in localStorage.");
        }
        const newRefreshToken = resData?.refresh_token || resData?.response?.refresh_token;
        if (newRefreshToken) {
          localStorage.setItem("e-OPD_refresh_token", newRefreshToken);
        }
        return newToken;
      } else {
        console.error("[e-OPD] Token refresh API returned failure:", resData);
        throw new Error(resData?.message || "Failed to refresh token");
      }
    })().finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

const isTokenExpiredResponse = (status, data) => {
  const message = data?.message;

  // Check specific token expiration/invalid messages
  const isMessageMatch =
    message === "Token expired." ||
    message === "Invalid token." ||
    (typeof message === "string" && (
      message.toLowerCase().includes("token expired") ||
      message.toLowerCase().includes("invalid token") ||
      message.toLowerCase().includes("jwt expired")
    ));

  if (isMessageMatch) {
    return true;
  }

  // Any 401 on e-OPD endpoint indicates authentication/token failure
  if (status === 401) {
    return true;
  }

  return false;
};

const handleTokenRefreshAndRetry = async (originalRequest) => {
  originalRequest._retry = true;
  const newToken = await refreshAuthToken();
  if (newToken) {
    if (originalRequest.headers) {
      if (typeof originalRequest.headers.set === 'function') {
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
      } else {
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      }
    } else {
      originalRequest.headers = { Authorization: `Bearer ${newToken}` };
    }

    // Inform user that token refreshed and proceeding further
    toast.success("Token refreshed successfully. Proceeding with your request...", {
      toastId: "eopd-token-refreshed",
      autoClose: 3000,
    });

    console.log("[e-OPD] Retrying original request automatically:", originalRequest.url);
    // Automatically re-calls the previous API that failed
    return eopdClient(originalRequest);
  }
  throw new Error("Failed to obtain new access token");
};

// Response interceptor to handle token expiry and refresh
eopdClient.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;
    if (
      response?.data &&
      isTokenExpiredResponse(response.status, response.data) &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/login") &&
      !originalRequest.url?.includes("/refresh")
    ) {
      console.warn("[e-OPD] Token expiry detected in response body:", response.data);
      try {
        return await handleTokenRefreshAndRetry(originalRequest);
      } catch (refreshErr) {
        toast.error("Session expired. Please log in again to continue.", {
          toastId: "eopd-token-refresh-failed",
          autoClose: 4000,
        });
        return Promise.reject(refreshErr);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;

    if (
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/login") &&
      !originalRequest.url?.includes("/refresh") &&
      isTokenExpiredResponse(status, data)
    ) {
      console.warn("[e-OPD] 401 Unauthorized detected for:", originalRequest.url, data);
      try {
        return await handleTokenRefreshAndRetry(originalRequest);
      } catch (refreshErr) {
        toast.error("Session expired. Please log in again to continue.", {
          toastId: "eopd-token-refresh-failed",
          autoClose: 4000,
        });
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export const eopdAPI = {
  login: async (data) => {
    const res = (await eopdClient.post('/login', data)).data;
    const user = res?.response || res?.data || res;
    if (user?.refresh_token) {
      localStorage.setItem("e-OPD_refresh_token", user.refresh_token);
    }
    return res;
  },
  refreshToken: async (token) => {
    const refreshToken = token || localStorage.getItem("e-OPD_refresh_token");
    const username = localStorage.getItem("e-OPD_username");
    const headers = { "Content-Type": "application/json" };
    if (username) {
      headers.username = username;
    }
    return (await axios.post('/nodeapi/eopd/refresh', { refresh_token: refreshToken }, { headers })).data;
  },
  logout: async () => (await eopdClient.post('/logout')).data,
  getPatients: async (params) => (await eopdClient.get('/patients', { params })).data,
  getDisplayId: async () => (await eopdClient.get('/opd/display-id')).data,
  getComplaintsAutocomplete: async (term, displayId) =>
    (await eopdClient.get('/complaints/autocomplete', { params: { term, display_id: displayId } })).data,
  getPathologyTests: async () => (await eopdClient.get('/pathology-tests')).data,
  getCounsellingOptions: async () => (await eopdClient.get('/counselling')).data,
  getSavedPackages: async (displayId) => (await eopdClient.get(`/opd/${displayId}/packages`)).data,
  searchDrugs: async (q) => (await eopdClient.get('/drugs/search', { params: { q } })).data,
  getVisitSummary: async (displayId) => (await eopdClient.get(`/opd/${displayId}`)).data,
  getPatientVisits: async (patientId) => (await eopdClient.get(`/opd/patient/${patientId}`)).data,
  getOpdStepData: async (displayId, step) => (await eopdClient.get(`/opd/${displayId}/steps/${step}`)).data,
  saveOpdStep: async (step, data) => (await eopdClient.post(`/opd?step=${step}`, data)).data,
};
